import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CVHttpService } from '../../services';
import CertificateFormDialog from '../dialogs/CertificateFormDialog';

function CertificatesStep({ cvId }) {
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCertificate, setEditingCertificate] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadCertificates();
        }
    }, [cvId]);

    const loadCertificates = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/certificate/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const certificatesList = Array.isArray(data) ? data : (data?.results || []);
            setCertificates(certificatesList);
        } catch (err) {
            console.error('Failed to load certificates', err);
            setCertificates([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this certificate?')) return;
        try {
            await CVHttpService.deleteCertificate(id);
            loadCertificates();
        } catch (err) {
            alert('Failed to delete certificate');
        }
    };

    const formatExpirationDate = (month, year) => {
        if (!month || !year) return 'No expiration';
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[month - 1]} ${year}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Certificates
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingCertificate(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Certificate
                </Button>
            </Stack>

            {!Array.isArray(certificates) || certificates.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No certificates added yet. Click "Add Certificate" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(certificates) && certificates.map((cert) => (
                        <Paper key={cert.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        {cert.title}
                                    </Typography>
                                    {cert.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {cert.description}
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap">
                                        {(cert.expiration_month || cert.expiration_year) && (
                                            <Chip 
                                                label={`Expires: ${formatExpirationDate(cert.expiration_month, cert.expiration_year)}`}
                                                size="small" 
                                                color={cert.expiration_year && cert.expiration_year < new Date().getFullYear() ? 'error' : 'default'}
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingCertificate(cert);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(cert.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <CertificateFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingCertificate(null);
                }}
                onSave={() => {
                    loadCertificates();
                    setOpenDialog(false);
                    setEditingCertificate(null);
                }}
                cvId={cvId}
                certificate={editingCertificate}
            />
        </Box>
    );
}

export default CertificatesStep;

